import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { inr } from "@/lib/format";
import { Panel, SectionShell } from "./_shared";
import { PawPrint, Sparkles } from "lucide-react";

// Deterministic business briefing composed from already-authoritative data.
// No fabricated AI, no Shally backend change.
export function ShallyBriefing({ canReports }) {
  const navigate = useNavigate();
  const dash = useQuery({ queryKey: ["dashboard"], queryFn: async () => (await api.get("/dashboard")).data });
  const health = useQuery({
    queryKey: ["business-health"], enabled: canReports,
    queryFn: async () => (await api.get("/reports/business-health")).data,
  });
  const ops = useQuery({
    queryKey: ["ops-dashboard"], enabled: canReports,
    queryFn: async () => (await api.get("/ops-dashboard")).data,
  });
  const q = { ...dash, isLoading: dash.isLoading || (canReports && (health.isLoading || ops.isLoading)) };

  return (
    <Panel className="overflow-hidden" data-testid="shally-briefing">
      <div className="pawkt-gradient-primary px-5 py-4 flex items-center gap-3">
        <div className="grid h-11 w-11 place-items-center rounded-full bg-white/20 ring-4 ring-white/10 backdrop-blur-sm">
          <PawPrint className="h-6 w-6 text-white" />
        </div>
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-white/80">Ask Shally</div>
          <div className="font-display text-base font-extrabold text-white">What's happening in my business?</div>
        </div>
      </div>
      <div className="p-5">
        <SectionShell query={q}>
          {(d) => {
            const rev = d.revenue || {};
            const top = (d.by_service || [])[0];
            const h = health.data;
            const cards = ops.data?.cards || {};
            const fu = (cards.overdue_followups?.value || 0) + (cards.pending_followups?.value || 0);
            const growth = (h && typeof h.revenue_growth_pct === "number") ? h.revenue_growth_pct : null;
            const lines = [];
            // interpretive opener (conclusions only where data supports them)
            if (growth !== null) {
              if (growth <= -5) lines.push(`Your business is quieter than last month, down ${Math.abs(growth)}%.`);
              else if (growth >= 5) lines.push(`Your business is busier than last month, up ${growth}%.`);
              else lines.push(`Your business is holding steady versus last month.`);
            } else if ((rev.month || 0) > 0) {
              lines.push(`You've collected ${inr(rev.month)} so far this month.`);
            } else {
              lines.push(`It's early days — I'll interpret your business as activity builds.`);
            }
            if (top && top.revenue > 0) lines.push(`${top.name} is contributing most of the activity.`);
            if ((rev.outstanding || 0) > 0 || fu > 0) {
              const bits = [];
              if ((rev.outstanding || 0) > 0) bits.push(`${inr(rev.outstanding)} is still to collect`);
              if (fu > 0) bits.push(`${fu} follow-up${fu === 1 ? "" : "s"} need attention`);
              lines.push(`Meanwhile, ${bits.join(" and ")}.`);
            }

            const prompts = [];
            if (fu > 0) prompts.push({ t: "Show follow-ups", to: "/follow-ups" });
            if (top && top.revenue > 0) prompts.push({ t: `Explore ${top.name}`, to: "/reports" });
            if ((rev.outstanding || 0) > 0) prompts.push({ t: "What's unpaid?", to: "/invoices" });
            if (prompts.length === 0) prompts.push({ t: "Show me revenue", to: "/reports" });
            return (
              <div>
                <div className="rounded-2xl bg-pawkt-soft border border-pawkt-border-soft px-4 py-3">
                  <p className="text-sm text-pawkt-text leading-relaxed">{lines.join(" ")}</p>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {prompts.map((p, i) => (
                    <button key={p.t} data-testid={`shally-prompt-${i}`} onClick={() => navigate(p.to)}
                      className="inline-flex items-center gap-1.5 rounded-full border border-pawkt-border bg-white px-3 py-1.5 text-xs font-bold text-pawkt-text hover:border-pawkt-primary hover:text-pawkt-primary-deep transition-colors">
                      <Sparkles className="h-3 w-3 text-pawkt-primary" /> {p.t}
                    </button>
                  ))}
                </div>
                {!canReports && <p className="mt-3 text-xs text-pawkt-text-disabled">A fuller briefing is available with reporting access.</p>}
              </div>
            );
          }}
        </SectionShell>
      </div>
    </Panel>
  );
}
